import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowRight, BadgeCheck, Calendar, Check, EyeOff, FileLock2, FileText, ShieldCheck, UserCheck } from 'lucide-react'
import AppShell from '../../components/b2b/AppShell'
import ConsentCheckboxes from '../../components/ConsentCheckboxes'
import { api, APIError } from '../../api/client'
import { useB2BBillingStore } from '../../stores/useB2BBillingStore'
import { LEGAL_VERSIONS } from '../../config/legalVersions'

/**
 * B2B Pricing — inside the workspace (RequireB2BAuth, see App.jsx).
 *
 * Plans come from `GET /api/b2b/billing/plans` (b2b.plans is the source of truth).
 * All plans share the same product surface; they differ only in the number of
 * screenings the workspace can generate.
 */
export default function PricingScreen() {
  const navigate = useNavigate()
  const plans = useB2BBillingStore((s) => s.plans)
  const loadingPlans = useB2BBillingStore((s) => s.loadingPlans)
  const loadPlans = useB2BBillingStore((s) => s.loadPlans)
  const loadSubscription = useB2BBillingStore((s) => s.loadSubscription)
  const currentPlanCode = useB2BBillingStore((s) => s.subscription?.plan?.plan_code || null)

  const [checkoutPlanCode, setCheckoutPlanCode] = useState(null)
  const [checkoutError, setCheckoutError] = useState('')
  const [pendingPlanCode, setPendingPlanCode] = useState(null)
  const [consent, setConsent] = useState({ terms_accepted: false })
  const updateConsent = (key, value) => setConsent((prev) => ({ ...prev, [key]: value }))

  useEffect(() => {
    loadPlans()
    loadSubscription()
  }, [loadPlans, loadSubscription])

  const handleChoose = async (planCode) => {
    setCheckoutError('')
    if (planCode === 'free') {
      navigate('/b2b/dashboard')
      return
    }
    if (planCode === currentPlanCode) {
      navigate('/b2b/billing')
      return
    }

    // Surface the subscription-terms checkbox before redirecting to Stripe Checkout.
    setPendingPlanCode(planCode)
    setConsent({ terms_accepted: false })
  }

  const handleConfirmCheckout = async () => {
    setCheckoutError('')
    const planCode = pendingPlanCode
    if (!planCode) return
    if (!consent.terms_accepted) {
      setCheckoutError('Please accept the Business Terms, DPA, and Refund Policy to continue.')
      return
    }
    setCheckoutPlanCode(planCode)
    try {
      const data = await api.post('/api/b2b/billing/checkout', {
        plan_code: planCode,
        billing_period: 'monthly',
        return_url: `${window.location.origin}/b2b/payment-result`,
        terms_accepted: true,
        terms_version: LEGAL_VERSIONS.BUSINESS_TERMS,
      })
      try { localStorage.setItem('lastB2BPaymentOrderReference', data.order_reference) } catch { /* ignore */ }
      window.location.href = data.checkout_url
    } catch (err) {
      const msg = err instanceof APIError ? (err.data?.detail || err.message) : 'Checkout failed'
      setCheckoutError(typeof msg === 'string' ? msg : 'Checkout failed')
      setCheckoutPlanCode(null)
    }
  }

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-6xl mx-auto">
        <p className="text-xs font-bold uppercase tracking-wider text-accent">Pricing</p>
        <h1 className="mt-1 text-3xl md:text-4xl font-bold text-ink-1">
          Pay per generated screening.
        </h1>
        <p className="mt-3 text-base text-ink-2 max-w-3xl">
          Every plan ships the same product. The only thing that changes between
          tiers is how many screenings you can generate. No setup fees, cancel
          anytime.
        </p>

        {checkoutError && (
          <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {checkoutError}
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {loadingPlans && plans.length === 0 ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-white rounded-3xl border border-black/5 p-6 animate-pulse h-72" />
            ))
          ) : (
            plans.map((plan) => (
              <PlanCard
                key={plan.plan_code}
                plan={plan}
                isCurrent={plan.plan_code === currentPlanCode}
                isProcessing={checkoutPlanCode === plan.plan_code}
                onChoose={() => handleChoose(plan.plan_code)}
              />
            ))
          )}
        </div>

        <CompliancePerks />

        <FaqStrip />

        {pendingPlanCode && (
          <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setPendingPlanCode(null) }}
          >
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-ink-1 mb-2">
                Confirm subscription
              </h3>
              <p className="text-sm text-ink-2 mb-4">
                You're about to subscribe to <strong>{pendingPlanCode}</strong>.
                Please accept our terms before we redirect you to the payment page.
              </p>
              <ConsentCheckboxes mode="subscribe" values={consent} onChange={updateConsent} />
              <div className="mt-6 flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setPendingPlanCode(null)}
                  className="px-4 py-2 rounded-xl text-sm font-semibold text-ink-2 hover:bg-canvas"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmCheckout}
                  disabled={!consent.terms_accepted || Boolean(checkoutPlanCode)}
                  className="bg-accent hover:bg-accent-hover disabled:opacity-50 text-white font-bold px-4 py-2 rounded-xl transition"
                >
                  {checkoutPlanCode ? 'Redirecting…' : 'Continue to payment'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  )
}

function PlanCard({ plan, isCurrent, isProcessing, onChoose }) {
  const isFree = plan.plan_code === 'free'
  const isRecommended = plan.is_recommended

  // Headline metric — "X screenings / month" or "X lifetime screenings".
  const screeningsLabel = plan.screenings_per_month != null
    ? `${plan.screenings_per_month} screenings / month`
    : `${plan.screenings_lifetime} lifetime screenings`

  const priceLabel = isFree ? '$0' : `$${plan.price_usd.toFixed(0)}`
  const priceSuffix = isFree ? '' : '/mo'

  const features = [
    screeningsLabel,
    plan.active_jobs_limit === -1 ? 'Unlimited active jobs' : `${plan.active_jobs_limit} active jobs`,
    plan.seats_limit === -1 ? 'Unlimited team members' : `${plan.seats_limit} team member${plan.seats_limit === 1 ? '' : 's'}`,
  ]

  let buttonLabel = 'Choose'
  if (isCurrent) buttonLabel = 'Current plan'
  else if (isProcessing) buttonLabel = 'Redirecting…'
  else if (isFree) buttonLabel = 'Stay on free'

  return (
    <div className={`relative bg-white rounded-3xl border p-6 flex flex-col ${
      isRecommended ? 'border-accent shadow-card-hover' : 'border-black/5 shadow-card'
    }`}>
      {isRecommended && (
        <div className="absolute -top-3 left-6 bg-accent text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
          Recommended
        </div>
      )}
      <div>
        <h2 className="text-xl font-bold text-ink-1">{plan.plan_name}</h2>
        {plan.tagline && <p className="text-xs text-ink-3 mt-1">{plan.tagline}</p>}
      </div>
      <div className="mt-5">
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold text-ink-1">{priceLabel}</span>
          <span className="text-sm text-ink-3">{priceSuffix}</span>
        </div>
      </div>

      <ul className="mt-5 space-y-2 text-sm text-ink-2 flex-1">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check className="w-4 h-4 mt-0.5 text-accent flex-shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={onChoose}
        disabled={isProcessing || isCurrent}
        className={`mt-6 w-full font-bold py-3 rounded-2xl transition flex items-center justify-center gap-2 ${
          isRecommended
            ? 'bg-accent hover:bg-accent-hover text-white'
            : 'bg-canvas hover:bg-black/5 text-ink-1'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {buttonLabel}
        {!isCurrent && !isProcessing && <ArrowRight className="w-4 h-4" />}
      </button>
    </div>
  )
}

function CompliancePerks() {
  const items = [
    { icon: EyeOff, label: 'No biometric scoring', detail: 'Text-only. Outside EU AI Act Art 5(1)(f).' },
    { icon: UserCheck, label: 'AI recommends, you decide', detail: 'GDPR Art 22 safe — no autonomous decisions.' },
    { icon: FileLock2, label: 'Tamper-evident audit log', detail: 'SHA-256 hashed prompts + responses per call.' },
    { icon: BadgeCheck, label: 'Versioned candidate consent', detail: 'Re-consent on policy change, enforced at DB level.' },
    { icon: Calendar, label: 'Configurable retention', detail: 'Choose 6 / 12 / 24 / 36 mo. Auto-delete.' },
    { icon: FileText, label: 'DPA + AI System Card', detail: 'Customer-accessible at signup. SCCs Module 2 + UK IDTA.' },
  ]
  return (
    <div className="mt-12 bg-white rounded-3xl border border-black/5 p-6 md:p-8">
      <div className="flex items-start gap-3">
        <div className="hidden md:flex w-10 h-10 shrink-0 rounded-xl bg-accent/10 text-accent items-center justify-center">
          <ShieldCheck className="w-5 h-5" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <p className="text-xs font-bold uppercase tracking-wider text-accent">Included on every plan</p>
          <h3 className="mt-1 text-lg md:text-xl font-bold text-ink-1">
            EU AI Act-ready compliance — no upcharge, no enterprise tier required.
          </h3>
          <p className="mt-2 text-sm text-ink-2 max-w-3xl leading-relaxed">
            Compliance shouldn’t be a tier upsell. Every Jobskills plan — including Free — ships with the same audit logging, consent flow, retention controls, and customer-accessible legal package.
          </p>
        </div>
      </div>

      <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
        {items.map((it) => {
          const Icon = it.icon
          return (
            <li key={it.label} className="flex items-start gap-3">
              <div className="w-9 h-9 shrink-0 rounded-lg bg-canvas border border-black/5 flex items-center justify-center text-accent">
                <Icon className="w-4 h-4" aria-hidden="true" />
              </div>
              <div>
                <p className="text-sm font-bold text-ink-1">{it.label}</p>
                <p className="mt-0.5 text-xs text-ink-3 leading-relaxed">{it.detail}</p>
              </div>
            </li>
          )
        })}
      </ul>

      <div className="mt-6 pt-6 border-t border-black/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-ink-3">
          EU AI Act high-risk obligations take effect 2 August 2026. Jobskills was designed for it.
        </p>
        <Link
          to="/b2b/compliance"
          className="inline-flex items-center gap-1.5 text-sm font-bold text-accent hover:text-accent-hover transition"
        >
          See the full compliance breakdown
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Link>
      </div>
    </div>
  )
}

function FaqStrip() {
  const items = [
    {
      q: 'What counts as a screening?',
      a: 'One screening = one candidate × one job. The screening is consumed from your quota the moment you save the questions and the candidate link goes out.',
    },
    {
      q: 'What is a team member?',
      a: 'A team member is a recruiter account with their own login that belongs to your workspace. Every team member sees the same jobs, candidates and screenings.',
    },
    {
      q: 'What happens when I run out of screenings?',
      a: 'You can keep using everything you already created. To send new screenings to candidates you upgrade to the next plan — no surprise charges, no automatic overages.',
    },
    {
      q: 'Can I cancel anytime?',
      a: 'Yes. Cancelling stops auto-renewal — your plan stays active until the end of the current billing period.',
    },
    {
      q: 'Do you offer a DPA / Data Processing Agreement?',
      a: 'Yes — published at /b2b/dpa and accepted as part of signup. Includes SCCs Module 2, UK IDTA, sub-processor annex, TOM annex, and 72-hour breach notification.',
    },
    {
      q: 'Is Jobskills EU AI Act-ready?',
      a: 'Yes. We don\'t process biometric data (outside Art 5(1)(f)). AI is a recommendation; your recruiter makes the hiring decision (GDPR Art 22 safe). Every AI call is logged with SHA-256 hashes. See /b2b/compliance for the full breakdown.',
    },
  ]
  return (
    <div className="mt-12 bg-white rounded-3xl border border-black/5 p-6">
      <h3 className="text-sm font-bold uppercase tracking-wider text-ink-3">Common questions</h3>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        {items.map((it) => (
          <div key={it.q}>
            <p className="text-sm font-bold text-ink-1">{it.q}</p>
            <p className="mt-1 text-sm text-ink-2">{it.a}</p>
          </div>
        ))}
      </div>
    </div>
  )
}
